/**
 * TaskList — flat, filterable list of tasks (default view).
 *
 * Features:
 *   - filter by status (multi), assignee, priority, search
 *   - inline edit of status (select)
 *   - delete + create-subtask quick actions
 *   - light theme only (CLAUDE.md hard rule)
 *
 * Note: a richer set of filters (status pills / priority / assignee) is also
 * provided at the *page* level (TasksPage). The local filter bar here is
 * still useful for the embedded use-case (e.g. dropping <TaskList/> directly
 * onto a room page) and is intentionally lightweight.
 */
import { useMemo, useState } from "react";

import {
  formatDate,
  isOverdue,
  priorityChipClass,
  statusChipClass,
} from "./format";
import {
  PRIORITY_LABELS_JA,
  STATUS_LABELS_JA,
  TASK_PRIORITIES,
  TASK_STATUSES,
} from "./types";
import type { Task, TaskPriority, TaskStatus, UpdateTaskInput } from "./types";

export interface TaskListProps {
  tasks: Task[];
  onUpdate?: (id: string, patch: UpdateTaskInput) => void;
  onDelete?: (id: string) => void;
  onSelect?: (task: Task) => void;
  emptyMessage?: string;
  /** When true, hide the embedded filter bar (parent provides its own). */
  hideFilters?: boolean;
  members?: { id: string; displayName: string }[];
  canUpdateTask?: (task: Task) => boolean;
  canDeleteTask?: (task: Task) => boolean;
}

interface Filters {
  search: string;
  statuses: Set<TaskStatus>;
  priority: TaskPriority | "all";
  assignee: string;
}

const INITIAL_FILTERS: Filters = {
  search: "",
  statuses: new Set<TaskStatus>(),
  priority: "all",
  assignee: "",
};

export function TaskList({
  tasks,
  onUpdate,
  onDelete,
  onSelect,
  emptyMessage = "タスクはまだありません。",
  hideFilters = false,
  members = [],
  canUpdateTask,
  canDeleteTask,
}: TaskListProps) {
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  const filtered = useMemo(() => {
    if (hideFilters) return tasks;
    const search = filters.search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (filters.statuses.size > 0 && !filters.statuses.has(t.status)) {
        return false;
      }
      if (filters.priority !== "all" && t.priority !== filters.priority) {
        return false;
      }
      if (filters.assignee && t.assignee !== filters.assignee) return false;
      if (search) {
        const hay = (t.title + "\n" + t.description).toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });
  }, [tasks, filters, hideFilters]);

  const assignees = useMemo(() => {
    const s = new Set<string>();
    for (const t of tasks) if (t.assignee) s.add(t.assignee);
    return Array.from(s).sort();
  }, [tasks]);

  return (
    <div className="flex flex-col gap-4" data-testid="task-list">
      {!hideFilters && (
        <div className="flex flex-wrap gap-3 rounded-md bg-canvas p-3 ring-1 ring-border-subtle">
          <input
            type="search"
            aria-label="タスクを検索"
            placeholder="タスクを検索..."
            className="grow min-w-[180px] rounded-md border border-border-default bg-canvas px-3 py-1.5 text-sm placeholder-fg-subtle focus-visible:shadow-focus"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
          />
          <select
            aria-label="優先度フィルタ"
            className="rounded-md border border-border-default bg-canvas px-2 py-1.5 text-sm focus-visible:shadow-focus"
            value={filters.priority}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                priority: e.target.value as TaskPriority | "all",
              }))
            }
          >
            <option value="all">優先度: すべて</option>
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS_JA[p]}
              </option>
            ))}
          </select>
          <select
            aria-label="担当者フィルタ"
            className="rounded-md border border-border-default bg-canvas px-2 py-1.5 text-sm focus-visible:shadow-focus"
            value={filters.assignee}
            onChange={(e) =>
              setFilters((f) => ({ ...f, assignee: e.target.value }))
            }
          >
            <option value="">担当: 全員</option>
            {assignees.map((a) => (
              <option key={a} value={a}>
                {members.find(member => member.id === a)?.displayName || a}
              </option>
            ))}
          </select>
          <div
            className="flex flex-wrap items-center gap-1.5"
            role="group"
            aria-label="ステータス絞り込み"
          >
            {TASK_STATUSES.map((s) => {
              const active = filters.statuses.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  aria-pressed={active}
                  onClick={() =>
                    setFilters((f) => {
                      const next = new Set(f.statuses);
                      if (next.has(s)) next.delete(s);
                      else next.add(s);
                      return { ...f, statuses: next };
                    })
                  }
                  className={
                    "rounded-full px-2.5 py-1 text-xs font-medium transition " +
                    (active
                      ? "bg-brand-500 text-fg-inverse ring-1 ring-brand-500"
                      : "bg-canvas text-fg-secondary ring-1 ring-border-subtle hover:bg-surface-raised")
                  }
                >
                  {STATUS_LABELS_JA[s]}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setFilters(INITIAL_FILTERS)}
            className="ml-auto rounded-md border border-border-default bg-canvas px-3 py-1.5 text-xs text-fg-secondary hover:bg-surface-raised"
          >
            リセット
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-default bg-canvas p-8 text-center text-sm text-fg-muted">
          {emptyMessage}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle overflow-hidden rounded-md bg-canvas shadow-sm ring-1 ring-border-subtle">
          {filtered.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              assigneeName={members.find(member => member.id === task.assignee)?.displayName}
              onUpdate={!canUpdateTask || canUpdateTask(task) ? onUpdate : undefined}
              onDelete={!canDeleteTask || canDeleteTask(task) ? onDelete : undefined}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface TaskRowProps {
  task: Task;
  assigneeName?: string;
  onUpdate?: (id: string, patch: UpdateTaskInput) => void;
  onDelete?: (id: string) => void;
  onSelect?: (task: Task) => void;
}

function TaskRow({ task, assigneeName, onUpdate, onDelete, onSelect }: TaskRowProps) {
  const overdue = isOverdue(task);
  return (
    <li
      data-testid="task-row"
      data-task-id={task.id}
      className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-base"
    >
      <button
        type="button"
        className="flex grow flex-col items-start gap-1 text-left"
        onClick={() => onSelect?.(task)}
      >
        <span className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${priorityChipClass(task.priority)}`}
          >
            {task.priority}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${statusChipClass(task.status)}`}
          >
            {STATUS_LABELS_JA[task.status]}
          </span>
          <span className="text-sm font-medium text-fg-primary">
            {task.title}
          </span>
          {task.parent_task_id && (
            <span className="text-[11px] text-fg-muted">サブタスク</span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-3 text-xs text-fg-muted">
          {task.assignee && (
            <span aria-label="担当">@{assigneeName || task.assignee}</span>
          )}
          <span
            aria-label="期限"
            className={overdue ? "text-danger" : ""}
            data-overdue={overdue ? "true" : undefined}
          >
            期限: {formatDate(task.due_at)}
          </span>
          {task.tags.length > 0 && (
            <span className="flex flex-wrap gap-1">
              {task.tags.map((t) => (
                <span
                  key={t}
                  className="rounded bg-surface-base px-1.5 py-0.5 text-[11px] text-fg-secondary ring-1 ring-border-subtle"
                >
                  #{t}
                </span>
              ))}
            </span>
          )}
        </span>
      </button>
      <select
        aria-label={`${task.title} のステータス`}
        disabled={!onUpdate}
        className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-fg-secondary focus-visible:shadow-focus"
        value={task.status}
        onChange={(e) =>
          onUpdate?.(task.id, { status: e.target.value as TaskStatus })
        }
      >
        {TASK_STATUSES.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABELS_JA[s]}
          </option>
        ))}
      </select>
      {onDelete && (
        <button
          type="button"
          aria-label={`${task.title} を削除`}
          onClick={() => onDelete(task.id)}
          className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-fg-secondary opacity-0 transition hover:bg-danger-bg hover:text-danger group-hover:opacity-100 focus:opacity-100"
        >
          削除
        </button>
      )}
    </li>
  );
}
